import React, { useState, useEffect, useMemo } from 'react'
import { withRouter, useParams, useLocation } from 'react-router-dom'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withLocalize, Translate } from 'react-localize-redux'
import { Grid, Box, Typography, Link } from '@material-ui/core'
import useGeoLocation from 'react-ipgeolocation'
import {
  getLocale,
  setLocale,
  getSignupURL,
  getLoginURL,
} from 'utils/localstorage'
import {
  auth as authModule,
  clubs as clubsModule,
  seasons as seasonsModule,
  users as usersModule,
  team as teamModule,
} from 'redux/modules'
import { fetchClubAgeClasses } from 'api/ageClasses/api'
import { identify } from 'utils/segmentTracking'
import { Snackbars as Snackbar } from 'components'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import isEmpty from 'lodash/isEmpty'
import * as Sentry from '@sentry/react'
import useStyles from '../styleComponents'
import SocialLogin from '../SocialLogin'
import { Header, SignupForm, TeamCreateForm, LegalText } from './components'
import RolesForm from './components/RolesForm'
import languages from 'constants/languages'
import { DEFAULT_CLUB_THEME } from 'constants/club'
import { TEAMS_ROUTE } from 'constants/routes'

const SignupPage = ({
  authState,
  dispatchSignup,
  setActiveLanguage,
  history,
  fetchClub,
  theme,
  fetchAuthenticatedUser,
  club,
  createTeamUnderClub,
  updateUser,
  usersState,
  teamState,
  fetchClubs,
  createUserClub,
  setAuthTokens,
}) => {
  // Get redux store theme when custom club signup otherwise its empty
  const currentTheme = isEmpty(theme) ? DEFAULT_CLUB_THEME : theme
  const isGoogleLoginEnabled =
    process.env.REACT_APP_ENABLE_GOOGLE_LOGIN === 'true'
  const isFacebookLoginEnabled =
    process.env.REACT_APP_ENABLE_FACEBOOK_LOGIN === 'true'

  const isSocialLoginEnabled = isGoogleLoginEnabled || isFacebookLoginEnabled

  const [isUserClubExisted, setIsUserClubExisted] = useState(false)

  const classes = useStyles(currentTheme)
  const { locale } = useParams() // locale from URL (optional)
  const { search } = useLocation() // URL query parameters
  const geoIP = useGeoLocation()

  const [snackbar, setSnackbar] = useState(false)
  const [country, setCountry] = useState('CH')
  const [ageClasses, setAgeClasses] = useState([])
  const [ageClassesLoading, setAgeClassesLoading] = useState(false)
  const query = useMemo(() => {
    return new URLSearchParams(search)
  }, [search])

  const customClubId = query.get('club')

  const getClubAgeClasses = (clubUuid) => {
    setAgeClassesLoading(true)
    fetchClubAgeClasses(clubUuid)
      .then((res) => setAgeClasses(res.data))
      .catch((error) => console.error('Error fetching age classes:', error))
      .finally(() => setAgeClassesLoading(false))
  }

  /**
   * Get preferred language from URL :locale parameter or set it to default (EN)
   * and store value in local storage.
   */
  useEffect(() => {
    if (languages.some((lang) => lang.code === locale)) {
      setLocale(locale)
      setActiveLanguage(locale)
    } else {
      setLocale('en')
      setActiveLanguage('en')
    }
  }, [locale])

  useEffect(() => {
    setCountry(geoIP.country)
  }, [geoIP])

  useEffect(() => {
    if (customClubId) {
      fetchClub(customClubId) // Used v1 api as v2 supported uuid instead of id
    }
  }, [customClubId])

  /**
   * Handle steps navigation.
   */
  useEffect(() => {
    const { token } = authState

    if (token) {
      fetchClubs({
        exclude_fallback: 1,
      }).then((data) => {
        const userClubs = data.response.data

        if (userClubs.length > 0) {
          history.push(TEAMS_ROUTE)

          return
        }

        // If user has no club then create new one and proceed to team creation step
        createUserClub().then((res) => {
          const userClub = res.response.data
          setIsUserClubExisted(true)

          getClubAgeClasses(userClub.uuid)
        })
        fetchAuthenticatedUser()
      })
    }

    if (
      !token &&
      (window.location.pathname.indexOf('/step2') > 0 ||
        window.location.pathname.indexOf('/step3') > 0)
    ) {
      history.push(getSignupURL())
    }
  }, [])

  const handleLanguage = (event) => {
    // @todo: fix after LanguageDropdown - replace event with value

    setLocale(event.target.value)
    setActiveLanguage(event.target.value)

    const params = new URLSearchParams(window.location.search).toString()
    history.push(params ? `${getSignupURL()}?${params}` : getSignupURL())
  }

  const handleSignup = (values) => {
    setSnackbar(false)
    Sentry.addBreadcrumb({
      category: 'ui.click',
      message: 'User clicked the Sign up button',
      data: {
        email: values.email,
      },
    })
    const params = {
      email: values.email,
      password: values.password,
      first_name: values.firstName,
      last_name: values.lastName || '',
      language: getLocale(),
      country,
      utm_source: query.get('utm_source'),
      utm_medium: query.get('utm_medium'),
      utm_campaign: query.get('utm_campaign'),
      utm_term: query.get('utm_term'),
      utm_content: query.get('utm_content'),
      time_zone: moment.tz.guess(),
    }

    dispatchSignup(params)
      .then(({ response }) => {
        const { access_token: accessToken, refresh_token: refreshToken } =
          response

        setAuthTokens({
          access_token: accessToken,
          refresh_token: refreshToken,
        })

        return fetchAuthenticatedUser()
      })
      .then((res) => {
        const userData = res.response.data

        identify(userData.uuid)
        setLocale(userData.language)
        setActiveLanguage(userData.language)

        return fetchClubs({ exclude_fallback: 1 })
      })
      .then((data) => {
        const userClubs = data.response.data

        if (userClubs.length > 0) {
          history.push(TEAMS_ROUTE)

          return null
        }

        const requestParams = {}
        const subscriptionPlanUuid = query.get('plan')

        // When user signup with existing club then pass club uuid to request body
        if (club) {
          requestParams.club_uuid = club.uuid
        }

        // When user signup with custom plan then create club using that plan
        if (subscriptionPlanUuid) {
          requestParams.subscription_plan_uuid = subscriptionPlanUuid
        }

        // If no user clubs then create new one and proceed to team creation step
        return createUserClub(requestParams).then((res) => {
          const userClub = res.response.data
          setIsUserClubExisted(true)

          history.push(`${getSignupURL()}/step2`)
          getClubAgeClasses(userClub.uuid)
        })
      })
      .catch((error) => {
        if (error?.errors) {
          const firstField = Object.keys(error.errors)[0]
          const errorMessage = error.errors[firstField]?.[0]
          setSnackbar({ message: errorMessage, error: true })
        } else {
          const fallbackMessage = error?.message
          setSnackbar({ message: fallbackMessage, error: true })
        }
        Sentry.captureException(error)
      })
  }

  const handleTeamCreate = (values) => {
    setSnackbar(false)

    const params = {
      name: values.teamName,
      age_class_uuid: values.ageClass,
      phone: values.phone,
      gender: 'm',
    }

    if (query.get('plan')) {
      params.subscription_plan_uuid = query.get('plan')
    }

    createTeamUnderClub(club.uuid, params)
      .then(() => {
        setIsUserClubExisted(false)

        history.push(`${getSignupURL()}/step3`)
      })
      .catch((error) => {
        setSnackbar({
          message: error,
          error: true,
        })
      })
  }

  const handleTeamRoles = (values) => {
    updateUser({ team_roles: values.teamRoles, id: authState.user.uuid }).then(
      () => {
        // User will be redirected to team dashboard page when hit base url
        history.push('/')
      }
    )
  }

  return (
    <Grid container spacing={0} className={classes.root}>
      <Grid item md={6}>
        <Box className={classes.centeredGridContent}>
          <Box className={classes.loginBox}>
            {authState.token && isUserClubExisted && (
              <>
                <Header
                  theme={currentTheme}
                  heading={<Translate id="signup.team-form-heading" />}
                  subHeading={<Translate id="signup.team-form-subheading" />}
                />
                <TeamCreateForm
                  ageClasses={ageClasses}
                  country={country}
                  onSubmit={handleTeamCreate}
                  inProgress={ageClassesLoading || teamState.isLoading}
                  theme={currentTheme}
                />
              </>
            )}
            {authState.token && !isUserClubExisted && teamState.uuid && (
              <>
                <Header
                  theme={currentTheme}
                  heading={<Translate id="signup.roles-form-heading" />}
                  subHeading={<Translate id="signup.roles-form-subheading" />}
                />
                <RolesForm
                  ageClasses={ageClasses}
                  country={country}
                  onSubmit={handleTeamRoles}
                  inProgress={usersState.isLoading}
                  theme={currentTheme}
                />
              </>
            )}
            {!authState.token && (
              <Box>
                <Header
                  theme={currentTheme}
                  heading={<Translate id="signup.signup" />}
                  subHeading={<Translate id="signup.welcome" />}
                />
                <SignupForm
                  language={getLocale()}
                  onLanguageChange={handleLanguage}
                  inProgress={authState.isLoading}
                  theme={currentTheme}
                  onSubmit={handleSignup}
                />
                {!customClubId && isSocialLoginEnabled && (
                  <SocialLogin
                    history={history}
                    page="login"
                    theme={currentTheme}
                    googleLink={<Translate id="signup.signup-with-google" />}
                    facebookLink={
                      <Translate id="signup.signup-with-facebook" />
                    }
                    isFacebookLoginEnabled={isFacebookLoginEnabled}
                    isGoogleLoginEnabled={isGoogleLoginEnabled}
                  />
                )}
                <Box mt={0.8} align="center">
                  <Typography
                    variant="body1"
                    align="center"
                    component="p"
                    className={classes.typography}
                  >
                    <span>
                      <Translate id="signup.do-have-account" />
                    </span>
                    &nbsp;
                    <Link href={getLoginURL()} underline="always">
                      <Translate id="signup.login" />
                    </Link>
                  </Typography>
                  <LegalText theme={currentTheme} />
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
      <Grid item xs={false} md={6}>
        <Box className={classes.background} />
      </Grid>

      {snackbar && <Snackbar {...snackbar} />}
    </Grid>
  )
}

SignupPage.defaultProps = {
  club: {},
  theme: {},
}
SignupPage.propTypes = {
  dispatchSignup: PropTypes.func.isRequired,
  createTeamUnderClub: PropTypes.func.isRequired,
  fetchClub: PropTypes.func.isRequired,
  fetchSeasons: PropTypes.func.isRequired,
  fetchAuthenticatedUser: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  authState: PropTypes.shape().isRequired,
  teamState: PropTypes.shape().isRequired,
  setActiveLanguage: PropTypes.func.isRequired,
  usersState: PropTypes.shape({
    isLoading: PropTypes.bool.isRequired,
  }).isRequired,
  theme: PropTypes.oneOfType([PropTypes.shape(), PropTypes.oneOf([null])]),
  club: PropTypes.shape(),
  updateUser: PropTypes.func.isRequired,
  createUserClub: PropTypes.func.isRequired,
  fetchClubs: PropTypes.func.isRequired,
  setAuthTokens: PropTypes.func.isRequired,
}

export default compose(
  withRouter,
  withLocalize,
  connect(
    ({ auth, team, clubs, users }) => ({
      authState: auth,
      teamState: team,
      theme: clubs.current ? clubs.current.theme : {},
      club: clubs.current,
      usersState: users,
    }),
    {
      dispatchSignup: authModule.signup,
      fetchClub: clubsModule.fetchClub,
      fetchClubs: clubsModule.fetchClubs,
      createUserClub: clubsModule.createUserClub,
      createTeamUnderClub: teamModule.createTeamByClub,
      fetchSeasons: seasonsModule.fetchSeasons,
      fetchAuthenticatedUser: authModule.fetchUser,
      updateUser: usersModule.updateUser,
      setAuthTokens: authModule.setAuthTokens,
    }
  )
)(SignupPage)
